import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { SERVICES } from "@/data/site";

export default function Services() {
  const [active, setActive] = useState(SERVICES[0].id);
  const activeService = SERVICES.find((s) => s.id === active);

  return (
    <section id="services" className="relative py-32 md:py-48" data-testid="services">
      <div className="container-mit">
        <div className="flex items-baseline justify-between mb-14">
          <div>
            <p className="text-[11px] tracking-[0.4em] text-[#A4801C] mb-4">— WHAT WE DO</p>
            <h2 className="font-display text-5xl md:text-7xl font-light leading-none text-[#0A0A14] max-w-3xl">
              Six practices.<br /> One outcome — <em className="text-gold-grad not-italic">transformation.</em>
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mt-10">
          {/* Index list */}
          <div className="lg:col-span-5">
            <ul className="divide-y divide-[#0A0A14]/12 border-t border-[#0A0A14]/12">
              {SERVICES.map((s) => (
                <li key={s.id}>
                  <button
                    onMouseEnter={() => setActive(s.id)}
                    onFocus={() => setActive(s.id)}
                    onClick={() => setActive(s.id)}
                    data-testid={`service-btn-${s.id}`}
                    className={`w-full flex items-baseline justify-between py-6 group text-left transition-colors ${
                      active === s.id ? "text-[#0A0A14]" : "text-[#0A0A14]/65 hover:text-[#0A0A14]"
                    }`}
                  >
                    <span className="flex items-baseline gap-6">
                      <span className="text-[11px] tracking-[0.3em] text-[#A4801C]/80">{s.number}</span>
                      <span className="font-display text-2xl md:text-3xl">{s.title}</span>
                    </span>
                    <span
                      className={`transition-all duration-500 ${
                        active === s.id ? "text-[#A4801C] translate-x-0 opacity-100" : "opacity-0 -translate-x-2"
                      }`}
                    >
                      →
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Preview panel */}
          <div className="lg:col-span-7">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeService.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.5 }}
                className="relative border border-[#0A0A14]/12 bg-[#EDE6D2]"
                data-testid={`service-panel-${activeService.id}`}
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={activeService.image}
                    alt={activeService.title}
                    className="w-full h-full object-cover"
                    style={{ filter: "grayscale(0.35) contrast(1.05)" }}
                  />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(30,27,75,0.15) 0%, rgba(30,27,75,0.55) 65%, rgba(30,27,75,0.9) 100%)" }} />
                  <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                    <div>
                      <p className="text-[10px] tracking-[0.3em] text-[#C99A2E]">{activeService.number} · {activeService.tagline}</p>
                      <h3 className="font-display text-3xl md:text-5xl text-[#FBF7EA] mt-2">{activeService.title}</h3>
                    </div>
                  </div>
                </div>
                <div className="p-8 md:p-10">
                  <p className="text-[#0A0A14]/70 text-lg leading-relaxed max-w-2xl mb-8">{activeService.excerpt}</p>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-8">
                    {activeService.items.map((it) => (
                      <li key={it} className="flex items-start gap-3 text-[#0A0A14]/75 text-sm">
                        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#1E1B4B] shrink-0" />
                        {it}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-10 flex items-center gap-4 flex-wrap">
                    <a
                      href={`#inquiry?service=${encodeURIComponent(activeService.title)}`}
                      className="btn-editorial"
                      data-testid={`service-inquire-${activeService.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        window.dispatchEvent(new CustomEvent("mit:prefillInquiry", { detail: { service: activeService.title } }));
                        document.getElementById("inquiry")?.scrollIntoView({ behavior: "smooth" });
                      }}
                    >
                      Inquire about this <span className="arrow">→</span>
                    </a>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
